import type { SolarProject } from "../types/solar";

/**
 * ProjectContext State Machine
 * 
 * Manages the lifecycle of the current active solar project:
 * 1. Load or create project from address
 * 2. Fetch project data (with retry logic)
 * 3. Poll until project is in terminal state (COMPLETED/ERROR)
 * 4. Expose current project + status
 */

export type ProjectContextState = {
  currentProject: SolarProject | null;
  isLoading: boolean;
  isPolling: boolean;
  error: string | null;
  pollingResult: "COMPLETED" | "ERROR" | "TIMEOUT" | null;
};

export type ProjectContextAction =
  | {
      type: "START_LOADING";
    }
  | {
      type: "PROJECT_LOADED";
      payload: SolarProject;
    }
  | {
      type: "PROJECT_CREATED";
      payload: SolarProject;
    }
  | {
      type: "LOADING_ERROR";
      payload: string;
    }
  | {
      type: "START_POLLING";
    }
  | {
      type: "POLLING_COMPLETED";
    }
  | {
      type: "POLLING_ERROR";
      payload: string;
    }
  | {
      type: "POLLING_TIMEOUT";
    }
  | {
      type: "CLEAR_ERROR";
    }
  | {
      type: "RESET";
    };

export const initialProjectContextState = (): ProjectContextState => ({
  currentProject: null,
  isLoading: false,
  isPolling: false,
  error: null,
  pollingResult: null,
});

/**
 * Reducer for ProjectContext state management
 * 
 * State transitions:
 * - START_LOADING: Begin loading/creating project
 * - PROJECT_LOADED: Found existing project in localStorage
 * - PROJECT_CREATED: Created new project from address
 * - LOADING_ERROR: Load or create phase failed
 * - START_POLLING: Begin polling for generation completion
 * - POLLING_COMPLETED: Generation complete
 * - POLLING_ERROR: Polling phase failed
 * - POLLING_TIMEOUT: Polling exceeded 3-minute limit
 * - CLEAR_ERROR: User dismisses error
 * - RESET: Reset to initial state
 */
export const projectContextReducer = (
  state: ProjectContextState,
  action: ProjectContextAction
): ProjectContextState => {
  switch (action.type) {
    case "START_LOADING":
      return {
        ...state,
        isLoading: true,
        error: null,
        pollingResult: null,
      };

    case "PROJECT_LOADED":
      return {
        ...state,
        currentProject: action.payload,
        isLoading: false,
        error: null,
      };

    case "PROJECT_CREATED":
      return {
        ...state,
        currentProject: action.payload,
        isLoading: false,
        error: null,
      };

    case "LOADING_ERROR":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case "START_POLLING":
      return {
        ...state,
        isLoading: false,
        isPolling: true,
        error: null,
      };

    case "POLLING_COMPLETED":
      return {
        ...state,
        isPolling: false,
        pollingResult: "COMPLETED",
        error: null,
      };

    case "POLLING_ERROR":
      return {
        ...state,
        isPolling: false,
        pollingResult: "ERROR",
        error: action.payload,
      };

    case "POLLING_TIMEOUT":
      return {
        ...state,
        isPolling: false,
        pollingResult: "TIMEOUT",
        error:
          "Die Projektgenerierung hat zu lange gedauert. Bitte versuche es erneut.",
      };

    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };

    case "RESET":
      return initialProjectContextState();

    default:
      return state;
  }
};
